import React, { useState } from 'react';

import { IFormFieldInputBaseProps } from '@components/form-fields';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { MAIN_THEME } from '@styles';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface IFormFieldWrapperProps extends IFormFieldInputBaseProps {
  name: string;
  title: string;
  material?: boolean;
  error?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  hideError?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  classes?: { label?: string };
}

const useStyles = makeStyles()((theme) => ({
  labelWrapper: {
    display: 'block',
    textAlign: 'left',
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    lineHeight: '15px'
  },
  label: {
    display: 'block',
    color: MAIN_THEME.color.black,
    textAlign: 'left',
    margin: '0 0 10px',
    fontFamily: MAIN_THEME.font.medium,
    fontSize: MAIN_THEME.fontSize.sm,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  materialLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '16px',
    fontFamily: MAIN_THEME.font.medium,
    margin: '0px',
    padding: '0px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0
  },
  description: {
    fontSize: MAIN_THEME.fontSize.xs,
    fontStyle: 'italic',
    marginLeft: '5px',
    color: MAIN_THEME.color.black
  },
  error: {
    textAlign: 'right',
    fontSize: MAIN_THEME.fontSize.xs,
    lineHeight: '14px',
    display: 'block',
    minHeight: '15px',
    color: MAIN_THEME.color.red,
    marginTop: '6px'
  },
  childrenContainer: { position: 'relative' },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    direction: 'ltr',
    gap: 6
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0
  },
  capsLockWarning: {
    color: MAIN_THEME.color.red,
    fontSize: MAIN_THEME.fontSize.xs,
    display: 'block',
    marginTop: '-15px',
    textAlign: 'left'
  },
  tooltipIcon: {
    marginLeft: '5px',
    cursor: 'pointer',
    width: '12px',
    height: '12px'
  },
  tooltip: {
    backgroundColor: 'white',
    color: 'black',
    boxShadow: theme.shadows[1],
    fontSize: MAIN_THEME.fontSize.xs,
    padding: '10px'
  },
  arrow: {
    color: 'white',
    '&::before': { boxShadow: theme.shadows[1] }
  },
  fullWidth: {
    gridColumn: '1 / -1',
    width: '100%'
  }
}));

const FormFieldWrapper: React.FC<IFormFieldWrapperProps> = ({
  className,
  error,
  style,
  name,
  title,
  hideError,
  fullWidth,
  children,
  hasTooltip = false,
  tooltipText,
  material = false
}) => {
  const { classes: labelClasses } = useStyles();
  const [showLabelTooltip, setShowLabelTooltip] = useState(false);

  const checkTruncation = (el: HTMLElement | null) =>
    !!el && el.scrollWidth > el.clientWidth + 1;

  return (
    <label
      htmlFor={name}
      className={clsx(
        labelClasses.labelWrapper,
        className,
        fullWidth && labelClasses.fullWidth
      )}
      style={style}
    >
      <div className={labelClasses.headerContainer}>
        <Tooltip
          title={title}
          placement="top"
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          open={showLabelTooltip}
        >
          <span
            className={
              material ? labelClasses.materialLabel : labelClasses.label
            }
            onMouseEnter={(e) =>
              setShowLabelTooltip(checkTruncation(e.currentTarget))
            }
            onMouseLeave={() => setShowLabelTooltip(false)}
            onFocus={(e) =>
              setShowLabelTooltip(checkTruncation(e.currentTarget))
            }
            onBlur={() => setShowLabelTooltip(false)}
          >
            {title}
          </span>
        </Tooltip>

        {hasTooltip && tooltipText !== '' && (
          <div className={labelClasses.actionsContainer}>
            <Tooltip
              title={tooltipText || ''}
              placement="top"
              classes={{
                tooltip: labelClasses.tooltip,
                arrow: labelClasses.arrow
              }}
              arrow
            >
              <InfoIcon className={labelClasses.tooltipIcon} />
            </Tooltip>
          </div>
        )}
      </div>

      <div className={labelClasses.childrenContainer}>{children}</div>

      {!hideError && (
        <span id={`errorMsg-${name}`} className={labelClasses.error}>
          {error}
        </span>
      )}
    </label>
  );
};

export default FormFieldWrapper;
